<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ContractDetailReadonly from './ContractDetailReadonly.vue';
import ContractDetailEditForm from './ContractDetailEditForm.vue';
import type { FetchContractResponse } from 'src/api/models/fetchContractResponse';
import { formatCurrency } from 'src/utils/currency';
import { FetchContractResponseStatus } from 'src/api/models/fetchContractResponseStatus';
import type { ContractFormData, ContractPatchData } from 'src/composables/useContractManagement';
import type { FetchOrganisationResponseLite } from 'src/api/models/fetchOrganisationResponseLite';
import type { FetchUnitResponseLite } from 'src/api/models/fetchUnitResponseLite';
import type { FetchAddressResponse } from 'src/api/models/fetchAddressResponse';
import type { NestedOrganisationContactPerson } from 'src/api/models/nestedOrganisationContactPerson';
import type { FetchRecipeResponseLite } from 'src/api/models/fetchRecipeResponseLite';

const props = defineProps<{
  contract: FetchContractResponse | null;
  loading: boolean;
  operationInProgress: boolean;
  searchOrganisationsFn: (query: string, page: number) => Promise<{ data: FetchOrganisationResponseLite[]; page: number; total_pages: number }>;
  searchUnitsFn: (orgId: string, query: string) => Promise<{ data: FetchUnitResponseLite[]; page: number; total_pages: number }>;
  searchBranchesFn: (orgId: string | null, query: string, page: number) => Promise<{ data: FetchAddressResponse[]; page: number; total_pages: number }>;
  searchContactPersonsFn: (orgId: string, query: string, page: number) => Promise<{ data: NestedOrganisationContactPerson[]; page: number; total_pages: number }>;
  searchRecipesFn: (query: string, page: number) => Promise<{ data: FetchRecipeResponseLite[]; page: number; total_pages: number }>;
}>();

const emit = defineEmits<{
  'update-contract': [id: string, data: ContractPatchData];
  'update-note': [id: string, note: string];
  'save-edit': [id: string, data: ContractFormData];
  'activate-contract': [id: string];
  'offer-contract': [id: string];
  'close': [];
}>();

const noteValue = ref('');
const noteDirty = ref(false);
const editing = ref(false);
const editFormDirty = ref(false);

const contractNumber = computed(() => props.contract?.number ?? '-');
const contractStatus = computed(() => props.contract?.status ?? FetchContractResponseStatus.new);
const actionItemsCount = computed(() => props.contract?.action_items?.length ?? 0);

const isEditableStatus = computed(() => {
  return contractStatus.value === FetchContractResponseStatus.new
    || contractStatus.value === FetchContractResponseStatus.offer_sent;
});

const isNewStatus = computed(() => contractStatus.value === FetchContractResponseStatus.new);
const canActivate = computed(() => contractStatus.value === FetchContractResponseStatus.offer_sent);
const canOffer = computed(() => contractStatus.value === FetchContractResponseStatus.new);

const missingOfferFields = computed<string[]>(() => {
  const contract = props.contract;
  if (!contract) return [];
  const missing: string[] = [];
  if (!contract.contract_holder?.id) missing.push('Contractant');
  if (!contract.operator?.id) missing.push('Locatie naam');
  if (!contract.unit?.id) missing.push('Unit');
  if (!contract.contract_holder_contact_person?.id) missing.push('Contact opdrachtgever');
  if (!contract.operator_branch?.id) missing.push('Locatie adres');
  // if (!contract.reference_number) missing.push('Referentie (PO-nummer)');
  if (!contract.type) missing.push('Contracttype');
  if (!contract.start_date) missing.push('Startdatum');
  if (!contract.period) missing.push('Contractperiode');
  if (!contract.action_items?.length) missing.push('Actiepunten');
  return missing;
});

const isOfferValid = computed(() => missingOfferFields.value.length === 0);

const offerTooltip = computed(() => {
  if (!missingOfferFields.value.length) return undefined;
  return {
    value: `Ontbrekende velden:\n• ${missingOfferFields.value.join('\n• ')}`,
    class: 'text-xs whitespace-pre',
  };
});

function onActivate() {
  if (!props.contract?.id) return;
  emit('activate-contract', props.contract.id);
}

function onOffer() {
  if (!props.contract?.id) return;
  emit('offer-contract', props.contract.id);
}

const isDirty = computed(() => noteDirty.value || editFormDirty.value);

function clearDirty() {
  noteDirty.value = false;
  editFormDirty.value = false;
}

watch(() => props.contract?.id, () => {
  editing.value = false;
  editFormDirty.value = false;
});

watch(editing, (val) => {
  if (!val) editFormDirty.value = false;
});

watch(() => props.contract, (contract) => {
  noteValue.value = contract?.note ?? '';
  noteDirty.value = false;
}, { immediate: true });

function onNoteUpdate(value: string | undefined) {
  noteValue.value = value ?? '';
  noteDirty.value = noteValue.value !== (props.contract?.note ?? '');
}

function saveNote() {
  if (!props.contract?.id || !noteDirty.value) return;
  emit('update-note', props.contract.id, noteValue.value);
}

function getStatusSeverity(status?: FetchContractResponseStatus | string): string {
  switch (status) {
    case FetchContractResponseStatus.new: return 'info';
    case FetchContractResponseStatus.offer_sent: return 'warn';
    case FetchContractResponseStatus.ended: return 'danger';
    default: return 'secondary';
  }
}

function onEditSave(data: ContractFormData) {
  if (!props.contract?.id) return;
  emit('save-edit', props.contract.id, data);
}

function onEditCancel() {
  editing.value = false;
  editFormDirty.value = false;
}

function exitEditMode() {
  editing.value = false;
}

defineExpose({ isDirty, clearDirty, exitEditMode });
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden" data-testid="contract-detail-panel">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <ProgressSpinner />
    </div>

    <template v-else-if="contract">
      <div class="flex items-center justify-between px-4 py-3 border-b border-surface-200 dark:border-surface-700 shrink-0">
        <div class="flex items-center gap-3 min-w-0">
          <span class="font-semibold text-lg whitespace-nowrap">Contract #{{ contractNumber }}</span>
          <Tag :severity="getStatusSeverity(contractStatus)" :value="String(contractStatus)" />
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Button
            v-if="isEditableStatus && !editing"
            data-testid="contract-edit-btn"
            icon="pi pi-pencil"
            label="Bewerken"
            severity="secondary"
            size="small"
            :disabled="operationInProgress"
            @click="editing = true"
          />
          <span
            v-if="canOffer && !editing"
            v-tooltip.top="offerTooltip"
            class="inline-block"
          >
            <Button
              data-testid="contract-offer-btn"
              icon="pi pi-check-circle"
              label="Aanbieding versturen"
              severity="success"
              size="small"
              :disabled="operationInProgress || !isOfferValid"
              @click="onOffer"
            />
          </span>
          <Button
            v-if="canActivate && !editing"
            data-testid="contract-activate-btn"
            icon="pi pi-check-circle"
            label="Contract activeren"
            severity="success"
            size="small"
            :disabled="operationInProgress"
            @click="onActivate"
          />
          <Button
            data-testid="contract-close-btn"
            icon="pi pi-times"
            severity="secondary"
            text
            rounded
            size="small"
            @click="emit('close')"
          />
        </div>
      </div>

      <div class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-5">
        <ContractDetailEditForm
          v-if="editing"
          :contract="contract"
          :saving="operationInProgress"
          :full-edit="isNewStatus"
          :search-organisations-fn="searchOrganisationsFn"
          :search-units-fn="searchUnitsFn"
          :search-branches-fn="searchBranchesFn"
          :search-contact-persons-fn="searchContactPersonsFn"
          :search-recipes-fn="searchRecipesFn"
          @save="onEditSave"
          @cancel="onEditCancel"
          @dirty-change="editFormDirty = $event"
        />
        <ContractDetailReadonly v-else :contract="contract" />

        <section v-if="!editing" data-testid="contract-section-recipes">
          <header class="contract-section-header">
            <span>Actiepunten</span>
            <span v-if="actionItemsCount > 0" class="contract-section-count tabular-nums">{{ actionItemsCount }}</span>
          </header>
          <div
            v-if="actionItemsCount === 0"
            class="text-sm text-color-secondary italic"
          >
            Geen actiepunten gekoppeld.
          </div>
          <ul v-else class="flex flex-col">
            <li
              v-for="(item, index) in contract.action_items"
              :key="item.id ?? index"
              class="contract-action-item"
              :data-testid="`contract-action-item-${index}`"
            >
              <Tag
                v-if="item.recipe_type"
                :data-testid="`contract-action-item-type-${index}`"
                severity="info"
                :value="item.recipe_type"
                class="shrink-0"
              />
              <span class="font-medium flex-1 min-w-0">{{ item.recipe_name ?? '-' }}</span>
              <span class="contract-action-item-meta tabular-nums">
                <span>{{ formatCurrency(item.price) }}</span>
                <span class="contract-action-item-sep">·</span>
                <span>{{ item.interval_in_months ?? '-' }} mnd</span>
              </span>
            </li>
          </ul>
        </section>

        <section v-if="!editing" data-testid="contract-section-note">
          <header class="contract-section-header">
            <span>Notitie</span>
            <span v-if="noteDirty" class="contract-section-flag">niet opgeslagen</span>
          </header>
          <Textarea
            data-testid="contract-note-input"
            :model-value="noteValue"
            placeholder="Voeg een notitie toe..."
            rows="4"
            auto-resize
            class="w-full"
            @update:model-value="onNoteUpdate"
            @keydown.ctrl.s.prevent="saveNote"
            @keydown.meta.s.prevent="saveNote"
          />
          <div class="flex justify-end mt-2">
            <Button
              data-testid="contract-note-save-btn"
              icon="pi pi-check"
              label="Opslaan"
              severity="success"
              size="small"
              :disabled="!noteDirty"
              :loading="operationInProgress"
              @click="saveNote"
            />
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<style scoped>
.contract-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--p-text-muted-color);
  padding-bottom: 0.375rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.contract-section-count {
  font-weight: 500;
  color: var(--p-text-muted-color);
}

.contract-section-flag {
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: var(--p-orange-500);
}

.contract-action-item {
  display: flex;
  align-items: baseline;
  gap: 0.625rem;
  padding: 0.4rem 0.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
  font-size: 0.875rem;
}

.contract-action-item:last-child {
  border-bottom: none;
}

.contract-action-item:hover {
  background-color: var(--p-content-hover-background);
  border-radius: 0.25rem;
}

.contract-action-item-meta {
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
}

.contract-action-item-sep {
  opacity: 0.6;
}
</style>
